import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, merge, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take } from 'rxjs/operators';
import _has from 'lodash-es/has';
import { FormFieldOption } from '@shared/shared/form-fields';
import { FormFieldsService } from '@shared/shared/services/form-fields.service';
import { FormFieldAbstractComponent } from '../abstract.component';
import { provideTranslation } from '@skyeng/libs/shared/i18n';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

const isFirstFocus = Symbol('isFirstFocus');
@DecorateUntilDestroy()
@Component({
  selector: 'crm-form-field-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  viewProviders: [provideTranslation('formFieldAutocomplete', () => import.meta.webpackContext!('./translation'))],
})
export class FormFieldAutocompleteComponent extends FormFieldAbstractComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('autocompleteInput') autocompleteInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatFormField) matFormField: MatFormField;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  options$: Observable<FormFieldOption[] | string[]>;
  multipleChoices: Map<string | number, FormFieldOption> = new Map<string | number, FormFieldOption>();
  autocompleteGroup = new UntypedFormGroup({
    autocomplete: new UntypedFormControl(),
  });

  onPasteHandler: (ClipboardEvent) => void;

  private onFocusSource = new Subject<symbol>();

  get autocompleteControl(): UntypedFormControl {
    return this.autocompleteGroup.get('autocomplete') as UntypedFormControl;
  }

  constructor(
    protected formFieldsService: FormFieldsService,
    private ref: ChangeDetectorRef,
  ) {
    super(formFieldsService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.initAutocomplete();
    this.initFieldStatus();
    this.initValue();
    this.initFieldReset();
    this.valueChangesFromModel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field && !changes.field.isFirstChange()) {
      this.initValue();
    }
  }

  ngOnDestroy(): void {}

  clearField($event): void {
    if (this.field.multiple) {
      this.multipleChoices.clear();
    }
    this.autocompleteControl.patchValue(null);

    super.clearField($event);
  }

  displayAutocomplete(option?: FormFieldOption | string | any): string {
    return _has(option, 'label') ? option.label : option;
  }

  removeMultiple(choice: string | number): void {
    this.multipleChoices.delete(choice);
    this.updateFieldValueMultiple();
  }

  selectedSingle(event: MatAutocompleteSelectedEvent): void {
    this.autocompleteControl.patchValue(event.option.value);
    this.getFormControl().patchValue(event.option.value);
  }

  selectedMultiple(event: MatAutocompleteSelectedEvent): void {
    const key = _has(event.option.value, 'label') ? event.option.value.value : event.option.value;

    this.multipleChoices.set(key, event.option.value);

    this.autocompleteInput.nativeElement.value = '';
    this.autocompleteControl.patchValue(null);

    this.updateFieldValueMultiple();
  }

  onFocus(): void {
    this.onFocusSource.next(isFirstFocus);
  }

  onBlur(event: FocusEvent): void {
    const classList = (event.relatedTarget as HTMLElement)?.classList;
    if (!classList) {
      return;
    }
    if (!classList.contains('mat-option')) {
      this.autocompleteTrigger.closePanel();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (typeof this.onPasteHandler === 'function') {
      this.onPasteHandler.call(this, event);
    }
  }

  private initAutocomplete(): void {
    const firstFocus = this.onFocusSource.asObservable().pipe(take(1));

    this.options$ = merge(firstFocus, this.autocompleteControl.valueChanges).pipe(
      map(value => {
        if (value === isFirstFocus) {
          this.getFormControl().markAsTouched();

          return this.field.multiple ? null : this.field.value;
        }
        return value;
      }),
      map((query: any) => (typeof query === 'string' ? query : query ? query.label : '')),
      filter(value => Boolean(this.field.autocompleteWithOptions) || (value != null && String(value).length > 1)),
      distinctUntilChanged(),
      switchMap(query => (typeof this.field.autocomplete === 'function' ? this.field.autocomplete(query) : [])),
      takeUntilDestroyed(this),
    );
  }

  private initFieldStatus(): void {
    if (this.field.disabled) {
      this.autocompleteControl.disable();
    }

    this.getFormControl()
      .statusChanges.pipe(takeUntilDestroyed(this))
      .subscribe(status => {
        if (status === 'DISABLED') {
          this.autocompleteControl.disable();
        } else {
          this.autocompleteControl.enable();
        }

        this.ref.detectChanges();

        if (status === 'INVALID') {
          this.autocompleteControl.setErrors(this.getFormControl().errors);
        } else if (status === 'VALID') {
          this.autocompleteControl.setErrors(null);
        }
      });
  }

  private initValue(): void {
    if (this.field.multiple) {
      const value = this.getFormControl().value;
      if (!Array.isArray(value)) {
        return;
      }

      const choices = value.map((val): [string | number, FormFieldOption] =>
        _has(val, 'label') ? [val.value, val] : [val, val],
      );
      this.multipleChoices = new Map(choices);

      return;
    }

    this.autocompleteControl.patchValue(this.getFormControl().value);
  }

  private updateFieldValueMultiple(): void {
    this.getFormControl().patchValue(Array.from(this.multipleChoices.values()));
  }

  private initFieldReset() {
    this.getFormControl()
      .valueChanges.pipe(
        startWith(this.field.value),
        pairwise(),
        filter(([prev, current]) => current == null && prev != null),
        takeUntilDestroyed(this),
      )
      .subscribe(_ => {
        if (this.field.multiple) {
          this.multipleChoices.clear();
        }
        this.autocompleteControl.patchValue(null);
      });
  }

  private valueChangesFromModel(): void {
    this.getFormControl()
      .valueChanges.pipe(
        startWith(this.field.value),
        filter(value => this.autocompleteControl.value !== value),
        takeUntilDestroyed(this),
      )
      .subscribe(_ => {
        if (!this.field.multiple) {
          this.autocompleteControl.patchValue(this.getFormControl().value, { emitEvent: false });
        }
      });
  }

  fakeSort(): number {
    return 0;
  }
}
